import { Immutable } from "immer";
import React from "react";
import { t } from "ttag";

import type { PopularProductsBlock as Value } from "@reactivated";

import { RichTextBlock } from "@thelabnyc/thelabui/src/components/RichText";

type Props = Immutable<{
    value: Value;
}>;

type ProductItem = NonNullable<Value["products"]>[number];

const renderPrice = (
    item: Immutable<ProductItem>,
): React.JSX.Element | null => {
    const info = item.purchase_info;
    if (!info || !info.price.exists) {
        return null;
    }
    const price = info.price;
    const cosmetic = price.cosmetic_excl_tax;
    if (!cosmetic) {
        return null;
    }
    const showStartingAt = !!item.show_starting_at;
    const retail = price.retail;
    const showRetail = retail !== null && retail !== cosmetic;
    if (!showRetail) {
        return (
            <p className="popular-products__card__price-elem--actual">
                {showStartingAt && <span>{t`Starting at `}</span>}
                {cosmetic}
            </p>
        );
    }
    return (
        <>
            <p className="popular-products__card__price-elem--retail">
                {showStartingAt && <span>{t`Starting at `}</span>}
                <span className="popular-products__card__price-elem--strike">
                    {retail}
                </span>
            </p>
            <p className="popular-products__card__price-elem--highlight">
                {cosmetic}
            </p>
        </>
    );
};

const renderProductCard = (
    item: Immutable<ProductItem>,
    index: number,
): React.JSX.Element => {
    const shopLinkUrl = item.shop_link?.url ?? "";
    const imageUrl = item.image?.url ?? "";
    const imageAlt = item.tagline ?? "";
    return (
        <div
            key={index}
            role="group"
            aria-label={item.name ? t`Product: ${item.name}` : t`product`}
            className="card product-card product-list__card popular-products__card popular-products__card--home-page"
        >
            <a href={shopLinkUrl} title={item.name ?? ""}>
                <h3 className="popular-products__card__category">
                    {item.tagline}
                </h3>
                <img
                    className="card__image popular-products__card__image responsive-img"
                    src={imageUrl}
                    alt={imageAlt}
                    loading="lazy"
                    decoding="async"
                />
                <div className="card__content popular-products__card__content">
                    <div className="popular-products__card__highlight">
                        {item.name_headline ? <p>{item.name_headline}</p> : " "}
                    </div>
                    <h3 className="card__title product-card__title popular-products__card__title">
                        {item.name}
                    </h3>
                    <div className="product-card__aux-copy popular-products__card__description">
                        <p>{item.description}</p>
                    </div>
                    <div className="product-card__price popular-products__card__price-elem">
                        {renderPrice(item)}
                    </div>
                </div>
            </a>
        </div>
    );
};

export const PopularProductsBlock = (
    props: Props,
): React.JSX.Element | null => {
    const v = props.value;
    const products = v.products ?? [];
    return (
        <div className="product-list popular-products">
            <div className="l-capped-width">
                <div className="product-list__header popular-products__header">
                    <div className="product-list__header-copy popular-products__header-copy">
                        <div className="product-list__title popular-products__title">
                            <RichTextBlock value={v.headline ?? ""} />
                        </div>
                    </div>
                </div>
                <div className="product-list__content popular-products__content u-flex-container">
                    {products.map(renderProductCard)}
                </div>
            </div>
        </div>
    );
};
